<template>
    <v-card class="article-card" :class="{ 'dark-mode': isDarkMode, 'linear-layout': layout === 'linear', [getArticleStatusClass]: true }" elevation="2">
        <!-- Header with SKU -->
        <div 
            class="article-header"
            :class="getArticleStatusClass"
            :data-status="getArticleStatusClass"
        >
            <div class="article-sku">
                {{ article.sku ? article.sku : article.name }}
            </div>
        </div>

        <!-- Product Image Area -->
        <div v-if="getImageUrl(article)" class="article-image" @click="openMediaModal">
            <v-img
                :src="getImageUrl(article)"
                class="product-image clickable"
                contain
                height="200"
            >
                <template v-slot:placeholder>
                    <div class="image-placeholder">
                        <v-icon size="48" color="grey">mdi-image</v-icon>
                    </div>
                </template>

                <!-- Quantity Badge - positioned absolutely on top left -->
                <div v-if="article.qty && article.qty > 1" class="qty-badge" :class="{ 'qty-high': article.qty > 1 }">
                    {{ article.qty }}x
                </div>

                <!-- Slot Badge - positioned absolutely on top right (packlist mode with shelf) -->
                <div v-if="mode === 'packlist' && article.shelf" class="shelf-slot-badge">
                    {{ article.shelf.slot }}
                </div>
                
                <!-- Scan Progress Badge - positioned absolutely on top right, below reset button -->
                <div v-if="article.scannedCount > 0 || pickingScannedCount > 0 || packingScannedCount > 0" class="scan-progress-badge">
                    <!-- Show mobile workflow phase label and counts if in mobile workflow -->
                    <template v-if="mobileWorkflowStep === 1 && pickingScannedCount > 0">
                        Picking: {{ pickingScannedCount }}/{{ article.qty || 1 }}
                    </template>
                    <template v-else-if="mobileWorkflowStep === 3 && packingScannedCount > 0">
                        Packing: {{ packingScannedCount }}/{{ article.qty || 1 }}
                    </template>
                    <!-- Show normal scan progress for desktop flow -->
                    <template v-else>
                        {{ article.scannedCount || 0 }}/{{ article.qty || 1 }}
                    </template>
                </div>

                <!-- Packlist Name Badge - shows which packlist the article is in (different packlist only) -->
                <div v-if="shouldShowPacklistBadge" class="packlist-name-badge">
                    📦 {{ articlePacklistName }}
                </div>
            </v-img>
            
            <!-- Dark Mode Indicator - positioned at bottom left of image container -->
            <div v-if="isDarkMode" class="dark-mode-indicator">
                <v-icon small color="white">mdi-weather-night</v-icon>
            </div>
        </div>

        <!-- No Image State (Clickable for Dark Mode) -->
        <div v-if="!getImageUrl(article)" class="no-image-area" @click="toggleDarkMode">
            <!-- Quantity Badge for cards without images -->
            <div v-if="article.qty && article.qty > 1" class="qty-badge-no-image">
                {{ article.qty }}x
            </div>

            <!-- Slot Badge for cards without images -->
            <div v-if="mode === 'packlist' && article.shelf" class="shelf-slot-badge-no-image">
                {{ article.shelf.slot }}
            </div>
            
            <!-- Scan Progress Badge for cards without images -->
            <div v-if="article.scannedCount > 0 || pickingScannedCount > 0 || packingScannedCount > 0" class="scan-progress-badge-no-image">
                <!-- Show mobile workflow phase label and counts if in mobile workflow -->
                <template v-if="mobileWorkflowStep === 1 && pickingScannedCount > 0">
                    Picking: {{ pickingScannedCount }}/{{ article.qty || 1 }}
                </template>
                <template v-else-if="mobileWorkflowStep === 3 && packingScannedCount > 0">
                    Packing: {{ packingScannedCount }}/{{ article.qty || 1 }}
                </template>
                <!-- Show normal scan progress for desktop flow -->
                <template v-else>
                    {{ article.scannedCount || 0 }}/{{ article.qty || 1 }}
                </template>
            </div>

            <!-- Packlist Name Badge for cards without images -->
            <div v-if="shouldShowPacklistBadge" class="packlist-name-badge-no-image">
                📦 {{ articlePacklistName }}
            </div>

            <!-- Dark Mode Indicator - positioned at bottom left -->
            <div v-if="isDarkMode" class="dark-mode-indicator">
                <v-icon small color="white">mdi-weather-night</v-icon>
            </div>
        </div>

        <!-- Linear Layout: SKU Section -->
        <div v-if="layout === 'linear'" class="linear-sku-section">
            <div class="linear-sku-label">SKU</div>
            <div class="linear-sku-value">{{ article.sku ? article.sku : article.name }}</div>
        </div>

        <!-- Gift Set Packaging Warning -->
        <div v-if="hasGiftSetPackaging" class="gift-set-warning" :class="{ 'linear-gift-warning': layout === 'linear' }">
            <v-icon small color="white" class="mr-1">mdi-alert</v-icon>
            <span v-if="layout === 'linear'">GESCHENKSET</span>
            <span v-else>GESCHENKSETVERPACKUNG</span>
        </div>

        <!-- Linear Layout: Slot Section (only if slot exists) -->
        <div v-if="layout === 'linear' && mode === 'packlist' && article.shelf" class="linear-slot-section">
            <div class="linear-slot-badge">
                {{ article.shelf.slot }}
            </div>
        </div>

        <!-- Article Properties (Grid layout or Linear personalization) -->
        <div class="article-details" :class="{ 'linear-personalization-section': layout === 'linear' }">
            <div v-if="layout === 'linear'" class="linear-personalization-label">PERSONALIZATION</div>

            <hr v-if="getFields(article).length > 0 && layout !== 'linear'">
            <div class="article-line"
                v-for="(field, i) in getFields(article)"
                :key="i"
            >
                <span class="line-label">{{ field.key }}:</span>
                <span class="line-text">{{ field.value || 'N/A' }}</span>
            </div>

            <div v-if="layout === 'linear' && getFields(article).length === 0" class="linear-no-personalization">
                No personalization
            </div>
        </div>

        <!-- Media Modal for Image Preview (print mode has dark/light toggle) -->
        <MediaModal
            v-model="showMediaModal"
            :items="mediaItems"
            mode="print"
            @close="closeMediaModal"
        />
    </v-card>
</template>

<script>
import MediaModal from './modals/MediaModal.vue';

export default {
    name: 'ArticleCard',
    components: {
        MediaModal
    },
    props: {
        article: {
            type: Object,
            required: true
        },
        packlistItems: {
            type: Array,
            default: () => []
        },
        mode: {
            type: String,
            default: 'multicart',
            validator: value => ['packlist', 'multicart'].includes(value)
        },
        nextItemToScan: {
            type: Object,
            default: null
        },
        isPartOfPacklistChecker: {
            type: Function,
            default: () => () => true
        },
        isInDifferentPacklistChecker: {
            type: Function,
            default: () => () => false
        },
        layout: {
            type: String,
            default: 'grid',
            validator: value => ['grid', 'linear'].includes(value)
        },

        // Mobile workflow state props (for desktop mirroring)
        mobileWorkflowStep: {
            type: Number,
            default: 1
        },
        pickingScannedArticles: {
            type: Object,
            default: () => ({})
        },
        packingScannedArticles: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            isDarkMode: false,
            showMediaModal: false
        }
    },
    computed: {
        mediaItems() {
            const imageUrl = this.getImageUrl(this.article);
            if (!imageUrl) return [];

            return [{
                url: imageUrl,
                label: this.article.sku,
                bg: this.isDarkMode ? '#000' : '#fff'
            }];
        },
        isPartOfCurrentPacklist() {
            // Use the passed function for consistency with other components
            return this.isPartOfPacklistChecker(this.article.sku);
        },

        // Mobile workflow helpers
        articleKey() {
            return `${this.article.sku}_${this.article.shelfHashcode || this.article.hashcode}`;
        },

        pickingScannedCount() {
            return this.pickingScannedArticles[this.articleKey] || 0;
        },

        packingScannedCount() {
            return this.packingScannedArticles[this.articleKey] || 0;
        },

        isScannedInPicking() {
            const requiredQty = this.article.qty || 1;
            return this.pickingScannedCount >= requiredQty;
        },

        isScannedInPacking() {
            const requiredQty = this.article.qty || 1;
            return this.packingScannedCount >= requiredQty;
        },

        mobileWorkflowPhaseLabel() {
            // Return the current phase label based on mobile workflow step
            if (this.mobileWorkflowStep === 1) {
                return 'Picking';
            } else if (this.mobileWorkflowStep === 2) {
                return 'Walk to Table';
            } else if (this.mobileWorkflowStep === 3) {
                return 'Packing';
            } else if (this.mobileWorkflowStep >= 4) {
                return 'Label Scan';
            }
            return '';
        },

        articlePacklistName() {
            // Get the packlist name (table) for this article from packlistItems
            if (!this.packlistItems || this.packlistItems.length === 0) {
                return null;
            }

            // Get designId from article
            const articleDesignId = this.article.designId || this.article.attributes?.designId;

            // Find matching packlist item by designId or SKU
            const packlistItem = this.packlistItems.find(item => {
                if (articleDesignId && item.designId) {
                    return item.designId === articleDesignId;
                }
                return item.sku === this.article.sku;
            });

            return packlistItem?.table || null;
        },

        shouldShowPacklistBadge() {
            // Show packlist badge only in packlist mode and if article is in a different packlist
            return this.mode === 'packlist' &&
                   this.articlePacklistName &&
                   this.isInDifferentPacklistChecker(this.article);
        },

        getArticleStatusClass() {
            // Unified status system across both modes

            // Mobile workflow mirroring: Check if article is scanned in current phase
            if (this.mobileWorkflowStep === 1 && this.isScannedInPicking) {
                // Picking phase - show as green if scanned in picking
                console.log('🎨 ArticleCard status: scanned in picking (green)', this.article.sku);
                return 'article-scanned';
            } else if (this.mobileWorkflowStep === 3 && this.isScannedInPacking) {
                // Packing phase - show as green if scanned in packing
                console.log('🎨 ArticleCard status: scanned in packing (green)', this.article.sku);
                return 'article-scanned';
            }

            // Normal desktop flow: 1. SCANNED - Green (both modes) - Actually scanned via QR code
            if (this.article.scanned === true || (this.article.scannedCount && this.article.scannedCount > 0)) {
                console.log('🎨 ArticleCard status: scanned (green)', this.article.sku);
                return 'article-scanned';
            }
            
            // 2. MISSING - Red (packlist mode only)
            if (this.mode === 'packlist' && (this.article.status === 'missing' || (!this.article.status && this.article.missing))) {
                console.log('🎨 ArticleCard status: missing (red)', this.article.sku);
                return 'article-missing';
            }

            // 3. IN DIFFERENT PACKLIST - Yellow (packlist mode only)
            // Items in a different packlist (but NOT multicart shelf items - those stay purple)
            if (this.mode === 'packlist' &&
                this.isInDifferentPacklistChecker(this.article) &&
                !this.article.shelf) {
                console.log('🎨 ArticleCard status: different packlist (yellow)', this.article.sku);
                return 'article-different-packlist';
            }

            // 4. IN MULTICART SHELF - Purple (packlist mode only)
            // Items in shelf have QR codes and need to be scanned
            if (this.mode === 'packlist' && this.article.shelf && this.article.status !== 'missing') {
                console.log('🎨 ArticleCard status: multicart shelf (purple)', this.article.sku);
                return 'article-multicart-shelf';
            }
            
            // 5. PART OF CURRENT PACKLIST - Green (packlist mode only)
            // Items that are part of current packlist don't have QR codes - they're ready by default
            if (this.mode === 'packlist' && this.isPartOfCurrentPacklist) {
                console.log('🎨 ArticleCard status: current packlist (green)', this.article.sku);
                return 'article-scanned'; // Green - ready without scanning
            }

            // 6. READY / NO STATUS - Orange (default for multicart mode)
            console.log('🎨 ArticleCard status: ready (orange)', this.article.sku);
            return 'article-ready';
        },

        hasGiftSetPackaging() {
            const giftPackagingSkus = ['zippo_gb_foto', 'zippo_gb_birthday_set', 'zippo_gb_whiskey_set', 'zippo_gb_oldtimer_set', 'zippo_gb_gentlemen_set']
            return this.article?.sku && giftPackagingSkus.includes(this.article.sku);
        }
    },
    methods: {
        getImageUrl(product) {
            let hasDesignId = product.designId || product.attributes?.designId;
            let hasPreview = product.attributes?.preview;

            if (hasDesignId) {
                return `https://accelery-poster.s3.eu-central-1.amazonaws.com/${encodeURIComponent(hasDesignId)}.png`;
            } else if (hasPreview) {
                return hasPreview[0]?.url || hasPreview;
            } else {
                let foundImageUrl = null;
                for (const [key, value] of Object.entries(product.attributes || {})) {
                    if (typeof value === 'string' && value.startsWith('http')) {
                        foundImageUrl = value;
                        break;
                    }
                }
                return foundImageUrl;
            }
        },
        getFields(product) {
            if (!product.translated || !product.attributes) return [];

            let blacklist = ['_', 'preview', 'designId', 'vQty', 'qty', 'grossPrice', 'discount'];

            return Object.entries(product.attributes)
                .filter(([key, value]) => {
                    // Check if value is not an HTTP URL and not null
                    const isValidValue = !value?.startsWith?.('http') && value !== null;
                    
                    // Check if key doesn't contain any blacklisted substrings
                    const isKeyAllowed = !blacklist.some(blockedStr => 
                        key.toLowerCase().includes(blockedStr.toLowerCase())
                    );
                    
                    return isValidValue && isKeyAllowed;
                })
                .map(([key, value]) => ({
                    key,
                    value
                }));
        },
        toggleDarkMode() {
            this.isDarkMode = !this.isDarkMode;
        },
        openMediaModal() {
            console.log('📸 Opening media modal for article:', this.article.sku);
            this.showMediaModal = true;
        },
        closeMediaModal() {
            console.log('❌ Closing media modal');
            this.showMediaModal = false;
        }
    }
}
</script>

<style scoped>
.article-card {
    width: 100%;
    max-width: 100%;
    margin: 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: auto;
    border-radius: 12px !important;
    overflow: hidden;
    position: relative;
}

.article-header {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 12px 16px;
    flex-shrink: 0;
    min-height: 60px;
    position: relative;
}

/* Unified Status Color System */
.article-header.article-ready {
    background-color: #ff9100; /* Orange - Ready/Default */
}

.article-header.article-missing {
    background-color: #f44336; /* Red - Missing */
    animation: pulse-missing 2s ease-in-out infinite;
}

.article-header.article-scanned {
    background-color: #4caf50; /* Green - Scanned */
}

.article-header.article-multicart-shelf {
    background-color: #9b59b6; /* Purple - In Multicart Shelf */
}

.article-header.article-different-packlist {
    background-color: #ffd700; /* Yellow - In Different Packlist */
}

/* Old individual status styles removed - now using unified system above */

.article-sku {
    font-size: 16px;
    font-weight: 600;
    color: white;
    text-align: center;
    padding: 6px 12px;
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
}

.article-image {
    background: #f8f8f8;
    width: 100%;
    overflow: hidden;
    flex-shrink: 0;
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease;
}

.article-image:hover {
    opacity: 0.95;
}

.product-image {
    width: 100%;
    height: 200px;
    object-fit: contain;
}

.product-image.clickable {
    cursor: pointer;
}

/* Dark Mode Indicator - positioned at bottom left of image */
.dark-mode-indicator {
    position: absolute;
    left: 8px;
    bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(33, 33, 33, 0.9);
    padding: 8px;
    border-radius: 50%;
    backdrop-filter: blur(6px);
    z-index: 15;
    pointer-events: none;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.5);
    border: 2px solid rgba(255, 255, 255, 0.2);
}

.image-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    background-color: #f0f0f0;
}

/* No Image Area - clickable for dark mode */
.no-image-area {
    background: #f8f8f8;
    width: 100%;
    min-height: 120px;
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
}

.no-image-area:hover {
    opacity: 0.95;
    background: #f0f0f0;
}

/* Quantity Badge - positioned on top left of image */
.qty-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    background-color: #f44336;
    color: white;
    font-size: 16px;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 4px;
    z-index: 10;
    animation: blinkRed 2s ease-in-out infinite;
}

.qty-badge.qty-high {
    background-color: #d32f2f;
    color: white;
    animation: blinkRed 1.5s ease-in-out infinite; /* Faster blink for high quantities */
}

/* Quantity Badge for cards without images */
.qty-badge-no-image {
    position: absolute;
    top: 8px;
    left: 8px;
    background-color: #f44336;
    color: white;
    font-size: 16px;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 4px;
    z-index: 10;
    animation: blinkRed 2s ease-in-out infinite;
}

/* Shelf Slot Badge - positioned on top right (packlist mode) */
.shelf-slot-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    background-color: #41254d;
    color: white;
    font-size: 42px;
    font-weight: 700;
    padding: 8px 12px;
    border-radius: 8px;
    z-index: 10;
    min-width: 40px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(155, 89, 182, 0.4);
    border: 2px solid rgba(255, 255, 255, 0.3);
}

.shelf-slot-badge-no-image {
    position: absolute;
    top: 8px;
    left: 8px;
    background-color: #41254d;
    color: white;
    font-size: 42px;
    font-weight: 700;
    padding: 8px 12px;
    border-radius: 8px;
    z-index: 10;
    min-width: 40px;
    text-align: center;
    box-shadow: 0 2px 8px rgba(155, 89, 182, 0.4);
    border: 2px solid rgba(255, 255, 255, 0.3);
}

/* Scan Progress Badge - positioned on top right, below reset button */
.scan-progress-badge {
    position: absolute;
    top: 48px;
    right: 8px;
    background-color: #2196F3;
    color: white;
    font-size: 14px;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 4px;
    z-index: 10;
    animation: blinkBlue 2s ease-in-out infinite;
}

/* Move scan progress badge up when reset button is hidden on tablet */
.scan-progress-badge-tablet {
    top: 8px !important; /* Move to where reset button would be */
}

/* Scan Progress Badge for cards without images */
.scan-progress-badge-no-image {
    position: absolute;
    top: 8px;
    right: 8px;
    background-color: #2196F3;
    color: white;
    font-size: 14px;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 4px;
    z-index: 10;
    animation: blinkBlue 2s ease-in-out infinite;
}

/* Packlist Name Badge - shows which packlist the article is in */
.packlist-name-badge {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background-color: #ffd700;
    color: #000;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 4px;
    z-index: 10;
    max-width: calc(100% - 16px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Packlist Name Badge for cards without images */
.packlist-name-badge-no-image {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background-color: #ffd700;
    color: #000;
    font-size: 12px;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 4px;
    z-index: 10;
    max-width: calc(100% - 16px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Gift Set Packaging Warning Badge */
.gift-set-warning {
    background-color: #d32f2f;
    color: white;
    font-size: 14px;
    font-weight: 700;
    padding: 12px 16px;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    border-top: 2px solid rgba(255, 255, 255, 0.2);
    border-bottom: 2px solid rgba(255, 255, 255, 0.2);
}

/* Linear layout version - compact column style */
.linear-gift-warning {
    flex-shrink: 0;
    width: fit-content;
    padding: 12px 20px;
    border: none;
    border-radius: 0;
    font-size: 13px;
    font-weight: 700;
}

/* Red blinking animation for quantity badges */
@keyframes blinkRed {
    0% { 
        background-color: #f44336;
        box-shadow: 0 0 5px rgba(244, 67, 54, 0.5);
    }
    50% { 
        background-color: #d32f2f;
        box-shadow: 0 0 15px rgba(244, 67, 54, 0.8), 0 0 25px rgba(244, 67, 54, 0.6);
    }
    100% { 
        background-color: #f44336;
        box-shadow: 0 0 5px rgba(244, 67, 54, 0.5);
    }
}

/* Blue blinking animation for scan progress badges */
@keyframes blinkBlue {
    0% { 
        background-color: #2196F3;
        box-shadow: 0 0 5px rgba(33, 150, 243, 0.5);
    }
    50% { 
        background-color: #1976D2;
        box-shadow: 0 0 15px rgba(33, 150, 243, 0.8), 0 0 25px rgba(33, 150, 243, 0.6);
    }
    100% { 
        background-color: #2196F3;
        box-shadow: 0 0 5px rgba(33, 150, 243, 0.5);
    }
}

.article-details {
    padding: 12px 16px;
    background-color: #f9f9f9;
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
}

.article-information-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #e1e1e1;
    padding: 8px 12px;
    border-radius: 8px;
}

.article-information-header-text {
    display: flex;
    align-items: center;
    gap: 8px;
}

.article-line {
    display: flex;
    font-size: 12px;
    width: 100%;
    flex-wrap: wrap;
    align-items: flex-start;
}

.line-label {
    font-weight: 600;
    color: #666;
    margin-right: 8px;
    min-width: 50px;
    flex-shrink: 0;
}

.line-text {
    color: #333;
    font-weight: 400;
    word-wrap: break-word;
    overflow-wrap: break-word;
    flex: 1;
    min-width: 0;
}

/* Pulse animation for missing items */
@keyframes pulse-missing {
    0% { 
        background-color: #f44336;
        box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
    }
    50% { 
        background-color: #d32f2f;
        box-shadow: 0 0 0 8px rgba(244, 67, 54, 0);
    }
    100% { 
        background-color: #f44336;
        box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
    }
}

/* Responsive adjustments */
@media (min-width: 600px) {
    .article-sku {
        font-size: 18px;
    }
    
    .line-label,
    .line-text {
        font-size: 14px;
    }
}

.copy-button:hover {
    transform: translateY(-1px);
}

/* Dark Mode Styles - Only affects image background in grid layout */
.article-card.dark-mode:not(.linear-layout) .article-image {
    background: #1a1a1a;
}

.article-card.dark-mode:not(.linear-layout) .no-image-area {
    background: #1a1a1a;
}

.article-card.dark-mode:not(.linear-layout) .no-image-area:hover {
    background: #222;
}

.article-card.dark-mode:not(.linear-layout) .image-placeholder {
    background-color: #1a1a1a;
}

/* Make image slightly dimmer in dark mode for better contrast */
.article-card.dark-mode:not(.linear-layout) .product-image {
    filter: brightness(0.9);
}

/* Dark Mode Styles for Linear Layout - Black background */
.article-card.linear-layout.dark-mode .article-image,
.article-card.linear-layout.dark-mode .no-image-area {
    background-color: #000 !important;
}

.article-card.linear-layout.dark-mode .product-image {
    filter: brightness(0.95) !important;
}

.article-card.linear-layout.dark-mode .image-placeholder {
    background-color: #000 !important;
}

/* Linear Layout - 4 Column horizontal: Preview | SKU | Slot | Personalization */
.article-card.linear-layout {
    flex-direction: row !important;
    align-items: stretch !important;
    min-height: 120px !important;
    height: 120px !important;
    overflow: hidden !important;
    position: relative !important;
    border-left: 6px solid transparent !important;
    gap: 0 !important;
}

/* Status color on left border */
.article-card.linear-layout.article-ready {
    border-left-color: #ff9100 !important;
}

.article-card.linear-layout.article-missing {
    border-left-color: #f44336 !important;
}

.article-card.linear-layout.article-scanned {
    border-left-color: #4caf50 !important;
}

.article-card.linear-layout.article-multicart-shelf {
    border-left-color: #9b59b6 !important;
}

.article-card.linear-layout.article-different-packlist {
    border-left-color: #ffd700 !important;
}

/* Hide the header in linear layout */
.article-card.linear-layout .article-header {
    display: none !important;
}

/* Column 1: Preview/Image - 120px */
.article-card.linear-layout .article-image,
.article-card.linear-layout .no-image-area {
    flex-shrink: 0 !important;
    width: 120px !important;
    min-width: 120px !important;
    max-width: 120px !important;
    height: 120px !important;
    min-height: 120px !important;
    max-height: 120px !important;
    margin-top: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    background-color: #f5f5f5 !important;
    border-right: 1px solid #e0e0e0 !important;
}

.article-card.linear-layout .product-image {
    height: 120px !important;
    max-height: 120px !important;
    width: 120px !important;
    object-fit: contain !important;
    padding: 10px !important;
}

/* Column 2: SKU Section - Fixed width for alignment */
.article-card.linear-layout .linear-sku-section {
    flex-shrink: 0 !important;
    width: 220px !important;
    min-width: 220px !important;
    max-width: 220px !important;
    height: 120px !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
    padding: 14px 18px !important;
    background-color: #fafafa !important;
    border-right: 1px solid #e0e0e0 !important;
}

.article-card.linear-layout .linear-sku-label {
    font-size: 11px !important;
    font-weight: 700 !important;
    color: #999 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    margin-bottom: 8px !important;
}

.article-card.linear-layout .linear-sku-value {
    font-size: 15px !important;
    font-weight: 700 !important;
    color: #333 !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
    line-height: 1.3 !important;
}

/* Column 3: Slot Section - fit-content */
.article-card.linear-layout .linear-slot-section {
    flex-shrink: 0 !important;
    width: fit-content !important;
    min-width: fit-content !important;
    height: 120px !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    padding: 14px 18px !important;
    background-color: #f5f5f5 !important;
    border-right: 1px solid #e0e0e0 !important;
}

.article-card.linear-layout .linear-slot-badge {
    background-color: #41254d !important;
    color: white !important;
    font-size: 38px !important;
    font-weight: 700 !important;
    padding: 10px 14px !important;
    border-radius: 8px !important;
    min-width: 55px !important;
    text-align: center !important;
    box-shadow: 0 2px 8px rgba(65, 37, 77, 0.4) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    line-height: 1 !important;
    white-space: nowrap !important;
}

/* Column 4: Personalization Section - Flexible width */
.article-card.linear-layout .linear-personalization-section {
    flex: 1 !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 12px 16px !important;
    height: 120px !important;
    max-height: 120px !important;
    box-sizing: border-box !important;
    overflow-y: auto !important;
    background-color: #fff !important;
}

.article-card.linear-layout .linear-personalization-label {
    font-size: 10px !important;
    font-weight: 700 !important;
    color: #999 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    margin-bottom: 6px !important;
    flex-shrink: 0 !important;
}

.article-card.linear-layout .linear-no-personalization {
    font-size: 14px !important;
    color: #999 !important;
    font-style: italic !important;
}

.article-card.linear-layout .article-details hr {
    display: none !important;
}

/* Personalization details - larger fonts, tighter spacing */
.article-card.linear-layout .article-line {
    display: flex !important;
    font-size: 14px !important;
    margin-bottom: 3px !important;
    gap: 6px !important;
    line-height: 1.3 !important;
}

.article-card.linear-layout .line-label {
    font-weight: 600 !important;
    color: #555 !important;
    min-width: auto !important;
    flex-shrink: 0 !important;
}

.article-card.linear-layout .line-text {
    color: #222 !important;
    font-weight: 500 !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    white-space: nowrap !important;
}

/* Hide details section in grid layout if it's empty */
.article-card:not(.linear-layout) .article-details:empty {
    display: none !important;
}

/* Always show details in linear layout (for SKU) */
.article-card.linear-layout .article-details {
    display: flex !important;
}

/* Badge positioning for linear layout */
.article-card.linear-layout .qty-badge {
    top: 6px !important;
    left: 6px !important;
    font-size: 12px !important;
    padding: 4px 8px !important;
}

/* Hide shelf slot badge in linear layout (shown in slot column instead) */
.article-card.linear-layout .shelf-slot-badge,
.article-card.linear-layout .shelf-slot-badge-no-image {
    display: none !important;
}

.article-card.linear-layout .scan-progress-badge {
    top: 6px !important;
    right: 6px !important;
    font-size: 11px !important;
    padding: 3px 6px !important;
}

.article-card.linear-layout .qty-badge-no-image {
    top: 6px !important;
    left: 6px !important;
    font-size: 12px !important;
    padding: 4px 8px !important;
}

.article-card.linear-layout .scan-progress-badge-no-image {
    top: 6px !important;
    right: 6px !important;
    font-size: 11px !important;
    padding: 3px 6px !important;
}

.article-card.linear-layout .packlist-name-badge {
    bottom: 6px !important;
    right: 6px !important;
    font-size: 10px !important;
    padding: 3px 6px !important;
}

.article-card.linear-layout .packlist-name-badge-no-image {
    bottom: 6px !important;
    right: 6px !important;
    font-size: 10px !important;
    padding: 3px 6px !important;
}

.article-card.linear-layout .dark-mode-indicator {
    bottom: 6px !important;
    left: 6px !important;
    padding: 4px !important;
}

/* Tablet-specific adjustments (768px - 1366px) */
@media (min-width: 768px) and (max-width: 1366px) {
    /* Linear layout tablet adjustments */
    .article-card.linear-layout {
        height: 110px !important;
        min-height: 110px !important;
    }

    .article-card.linear-layout .article-image,
    .article-card.linear-layout .no-image-area {
        width: 110px !important;
        min-width: 110px !important;
        max-width: 110px !important;
        height: 110px !important;
        min-height: 110px !important;
        max-height: 110px !important;
    }

    .article-card.linear-layout .product-image {
        height: 110px !important;
        max-height: 110px !important;
        width: 110px !important;
        padding: 8px !important;
    }

    /* Linear SKU section */
    .article-card.linear-layout .linear-sku-section {
        width: 200px !important;
        min-width: 200px !important;
        max-width: 200px !important;
        padding: 12px 16px !important;
        height: 110px !important;
    }

    .article-card.linear-layout .linear-sku-label {
        font-size: 10px !important;
        margin-bottom: 7px !important;
    }

    .article-card.linear-layout .linear-sku-value {
        font-size: 14px !important;
    }

    /* Linear Slot section */
    .article-card.linear-layout .linear-slot-section {
        width: fit-content !important;
        min-width: fit-content !important;
        padding: 12px 16px !important;
        height: 110px !important;
    }

    .article-card.linear-layout .linear-slot-badge {
        font-size: 32px !important;
        padding: 8px 12px !important;
        min-width: 48px !important;
    }

    /* Linear Personalization section */
    .article-card.linear-layout .linear-personalization-section {
        padding: 10px 14px !important;
        height: 110px !important;
        max-height: 110px !important;
    }

    .article-card.linear-layout .linear-personalization-label {
        font-size: 9px !important;
        margin-bottom: 5px !important;
    }

    .article-card.linear-layout .article-line {
        font-size: 13px !important;
        margin-bottom: 2px !important;
        gap: 5px !important;
        line-height: 1.3 !important;
    }

    .article-card.linear-layout .line-label,
    .article-card.linear-layout .line-text {
        font-size: 13px !important;
    }

    .article-card.linear-layout .linear-no-personalization {
        font-size: 13px !important;
    }

    /* Dark mode for linear layout on tablet */
    .article-card.linear-layout.dark-mode .article-image,
    .article-card.linear-layout.dark-mode .no-image-area {
        background-color: #000 !important;
    }

    .article-card.linear-layout.dark-mode .product-image {
        filter: brightness(0.95) !important;
    }

    .article-card.linear-layout.dark-mode .image-placeholder {
        background-color: #000 !important;
    }

    /* Grid layout tablet adjustments */
    .article-card:not(.linear-layout) .article-header {
        min-height: 50px !important;
        padding: 10px 12px !important;
    }

    .article-card:not(.linear-layout) .article-sku {
        font-size: 14px !important;
        padding: 4px 10px !important;
    }

    .article-card:not(.linear-layout) .article-details {
        padding: 10px 12px !important;
        gap: 4px !important;
    }

    .article-card:not(.linear-layout) .line-label,
    .article-card:not(.linear-layout) .line-text {
        font-size: 12px !important;
    }

    .article-card:not(.linear-layout) .article-line {
        font-size: 11px !important;
    }
}
</style>